import { getParentPath } from '@/components/document/sub-components/file-manager/utils';
import type { FileSystemItem } from '@/components/document/sub-components/file-manager/types';
import { withRetry } from '@/utils/withRetry';
import {
  GraphRequestError,
  graphError,
  parseRetryAfterMs,
} from '../graph/graph-request-error';
import { encodePathSegments } from '../graph/encode-path-segments';
import { GRAPH_RETRY_OPTIONS, RETRYABLE_STATUSES } from '../graph/graph-retry';
import { uploadInChunks } from '../graph/upload-in-chunks';
import { sharePointAuthService } from '../sharepoint-auth/sharepoint-auth-service';
import { CopyMonitorError } from './copy-monitor-error';

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

// Graph requires chunk sizes to be a multiple of 320 KiB. 5 MiB keeps chunk count (and therefore
// request count) reasonable for the multi-MB photos/videos/CAD drawings this file-manager
// realistically deals with, while staying well clear of any single-request size limits.
export const UPLOAD_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;

// Copy is the one Graph operation here that is asynchronous: the POST returns 202 and the work is
// then observed through a monitor resource. These bound that observation — not the copy itself,
// which SharePoint finishes server-side whether or not anyone is still watching.
const COPY_POLL_INITIAL_DELAY_MS = 1000;
const COPY_POLL_MAX_DELAY_MS = 10_000;
const COPY_POLL_BACKOFF_FACTOR = 1.5;
// Per item, not per batch. Long enough that a multi-GB folder does not trip it in normal use, short
// enough that a genuinely stuck copy does not hold a dimmed row for the rest of the session.
export const COPY_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Builds the correct Microsoft Graph drive-addressing endpoint for a path within one drive.
 *
 * Addressed as `/drives/{driveId}/...`, never `/sites/{siteId}/drive/...`: a project's site holds two
 * provisioned document libraries (D9), each its own drive, and the latter form silently means "the
 * site's *default* drive" — i.e. whichever library SharePoint happens to consider default.
 *
 * `parentRest` is a path *within* the drive, not a virtual file-manager path — the mount prefix has
 * already been stripped by the caller (see file-manager/logic/core/useFileManagerMounts.ts#resolveMountPath).
 * A drive root
 * ("/") uses the shorter id-less `root/{suffix}` form; nested paths and every upload target use the
 * path-addressed `root:{path}:/{suffix}` form. When a `fileName` is supplied the item being addressed
 * is `{parentRest}/{fileName}` (used by the upload-session URL), which always takes the colon form
 * even at the root.
 *
 * All path segments and the file name are URL-encoded here (via encodePathSegments /
 * encodeURIComponent) so this is the single place the encoding fix lives — all three call
 * sites inherit it.
 */
function buildDriveEndpoint(
  driveId: string,
  parentRest: string,
  suffix: string,
  fileName?: string,
): string {
  const drivePrefix = `${GRAPH_BASE_URL}/drives/${driveId}`;

  // Drive root children: nothing to address by path, so use the id-less form.
  // Never reached when fileName is set — an upload always addresses by path.
  if (parentRest === '/' && fileName === undefined) {
    return `${drivePrefix}/root/${suffix}`;
  }

  const encodedParent =
    parentRest === '/' ? '' : encodePathSegments(parentRest);
  const encodedFile =
    fileName === undefined ? '' : `/${encodeURIComponent(fileName)}`;

  return `${drivePrefix}/root:${encodedParent}${encodedFile}:/${suffix}`;
}

interface GraphUploadSession {
  uploadUrl: string;
}

interface GraphDriveItem {
  id?: string;
  name?: string;
  folder?: { childCount?: number };
  parentReference?: { driveId?: string; id?: string };
  size?: number;
  lastModifiedDateTime?: string;
  lastModifiedBy?: { user?: { displayName?: string } };
  webUrl?: string;
}

/** The two ids needed to address items on a drive by id rather than by path. */
export interface DriveRoot {
  driveId: string;
  rootId: string;
}

/** The two ids needed to address a specific driveItem (e.g. a move destination) by id. */
export interface ItemReference {
  driveId: string;
  id: string;
}

/**
 * A virtual file-manager path already resolved to the drive it lives on: `driveId` plus `rest`, the
 * path *within* that drive. `virtual` is the original path the file manager speaks, carried along so
 * returned FileSystemItems get virtual paths (everything above this service — navigation, selection,
 * drag, query keys — only ever knows virtual paths).
 *
 * The path-addressed methods below take this object rather than three positional strings on purpose:
 * two path spaces now coexist inside them, and a positional mix-up between `rest` and `virtual`
 * would be silent. Built by the file manager's mount provider
 * (file-manager/logic/core/useFileManagerMounts.ts#resolveMountPath); this service only ever
 * consumes it and never learns what a "mount" is.
 */
export interface MountTarget {
  driveId: string;
  rest: string;
  virtual: string;
}

interface GraphDriveChildrenResponse {
  value: GraphDriveItem[];
}

/** The monitor resource an async copy is observed through. */
interface GraphCopyMonitor {
  status?: 'notStarted' | 'inProgress' | 'completed' | 'failed';
  /** The new item's id — present only once status is 'completed'. */
  resourceId?: string;
  error?: { code?: string; message?: string };
}

interface GraphThumbnailSet {
  large?: { url?: string };
}

interface GraphThumbnailResponse {
  value: GraphThumbnailSet[];
}

export interface FileItemDetails {
  size?: number;
  lastModifiedDateTime?: string;
  downloadUrl?: string;
}

export interface FilePreview {
  getUrl: string;
  postUrl?: string;
  postParameters?: string;
}

interface GraphDriveItemDetails {
  size?: number;
  lastModifiedDateTime?: string;
  '@microsoft.graph.downloadUrl'?: string;
}

/**
 * Abort-aware delay for the copy monitor's backoff. Rejects immediately (rather than resolving late)
 * when the file manager unmounts mid-poll, so the poll loop exits on the same 'abandoned' path as an
 * aborted fetch instead of waking up to touch a torn-down component.
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new CopyMonitorError('abandoned'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new CopyMonitorError('abandoned'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function toFileSystemItem(
  item: GraphDriveItem,
  parentPath: string,
): FileSystemItem {
  const name = item.name ?? '';
  const path = parentPath === '/' ? `/${name}` : `${parentPath}/${name}`;
  const driveId = item.parentReference?.driveId;
  const id = item.id;
  const parentId = item.parentReference?.id;
  // Directory-listing metadata the details (table) view surfaces. Directories carry them too
  // (the table shows Modified/Modified-by for folders); the Size cell is blanked in the view
  // layer, not here. lastModifiedBy is flattened from the identitySet to just the display name.
  const size = item.size;
  const lastModifiedDateTime = item.lastModifiedDateTime;
  const lastModifiedBy = item.lastModifiedBy?.user?.displayName;
  const webUrl = item.webUrl;

  if (item.folder) {
    const childCount = item.folder.childCount ?? 0;
    return {
      type: 'directory',
      name,
      path,
      driveId,
      id,
      parentId,
      size,
      lastModifiedDateTime,
      lastModifiedBy,
      webUrl,
      childCount,
      hasFolderChildren: childCount > 0,
    };
  }

  return {
    type: 'file',
    name,
    path,
    driveId,
    id,
    parentId,
    size,
    lastModifiedDateTime,
    lastModifiedBy,
    webUrl,
  };
}

/**
 * Replaces the retired app-only-cert-backed server hop (sharepoint-drive-controller.ts) —
 * this now calls Microsoft Graph directly from the client, using the delegated
 * Sites.Selected token from sharepoint-auth-service.ts. No server involvement at all, which
 * also means no server-side body-size/proxying concern once upload is added later.
 */
class SharePointDriveService {
  // Caches the in-flight/resolved PROMISE (not just the resolved value) per driveId, so concurrent
  // callers during the same tick dedupe onto a single fetch instead of racing multiple requests.
  // Keyed per driveId, not per siteId: a site has two drives (D9) and each has its own root item.
  #driveRootCache = new Map<string, Promise<DriveRoot>>();

  async #fetchDriveRoot(driveId: string): Promise<DriveRoot> {
    const token = await sharePointAuthService.acquireGraphToken();

    // The root item itself — not a path/suffix under it — so this is addressed directly rather
    // than via buildDriveEndpoint.
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/root`;

    const body: GraphDriveItem = await withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw graphError('Failed to fetch SharePoint drive root', response);
      }

      return (await response.json()) as GraphDriveItem;
    }, GRAPH_RETRY_OPTIONS);

    return {
      driveId: body.parentReference?.driveId ?? '',
      rootId: body.id ?? '',
    };
  }

  async getDriveRoot(driveId: string): Promise<DriveRoot> {
    const cached = this.#driveRootCache.get(driveId);
    if (cached) return cached;

    const promise = this.#fetchDriveRoot(driveId);
    this.#driveRootCache.set(driveId, promise);
    // Failures must not stick around as a memoised rejection — evict so a later call re-fetches.
    // Load-bearing for the library-accessibility probe too: a transient failure resolving one
    // library's root must not permanently hide that library for the rest of the session.
    promise.catch(() => this.#driveRootCache.delete(driveId));

    return promise;
  }

  async getChildren(target: MountTarget): Promise<FileSystemItem[]> {
    const token = await sharePointAuthService.acquireGraphToken();

    // Addressed by path within the drive, not by driveItem id — this lines up with the
    // file-manager's existing path-string-based navigation with no contract change needed there. The
    // explicit $select guarantees (and trims to) the fields the grid/list rely on plus the three the
    // details (table) view needs; lastModifiedBy is a regular identitySet property that $select
    // alone returns as user.displayName, so no $expand is needed.
    const endpoint = `${buildDriveEndpoint(target.driveId, target.rest, 'children')}?$select=id,name,folder,parentReference,size,lastModifiedDateTime,lastModifiedBy,webUrl`;

    const body: GraphDriveChildrenResponse = await withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw graphError('Failed to list SharePoint drive children', response);
      }

      return (await response.json()) as GraphDriveChildrenResponse;
    }, GRAPH_RETRY_OPTIONS);

    // Re-parented under the VIRTUAL path, not `rest`: everything above this service navigates,
    // selects and caches by virtual path.
    return body.value.map((item) => toFileSystemItem(item, target.virtual));
  }

  async createFolder(
    target: MountTarget,
    name: string,
  ): Promise<FileSystemItem> {
    const token = await sharePointAuthService.acquireGraphToken();

    const endpoint = buildDriveEndpoint(target.driveId, target.rest, 'children');

    const created: GraphDriveItem | null = await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });

      if (response.ok) return (await response.json()) as GraphDriveItem;

      // A 409 here means a folder with this name already exists at this path. Since this
      // file-manager treats an existing folder as just as usable as one we'd have created
      // ourselves (letting repeated folder-tree uploads merge into the same destination instead
      // of failing), this is treated as success — but the 409 body is an error payload, not the
      // item, so the existing folder is fetched by path below to recover its real id/driveId.
      if (response.status === 409) return null;

      throw graphError('Failed to create SharePoint folder', response);
    }, GRAPH_RETRY_OPTIONS);

    if (created) return toFileSystemItem(created, target.virtual);

    // 409 path: GET the already-existing folder so callers get its real id/driveId/parentId
    // (needed by move/details, which address by id) rather than an id-less stub. Addressed with the
    // in-drive `rest` path, while the returned item is re-parented under the virtual one.
    const folderRest = target.rest === '/' ? `/${name}` : `${target.rest}/${name}`;
    const existing = await this.#getDriveItemByPath(
      target.driveId,
      folderRest,
      'Failed to resolve existing SharePoint folder after 409',
    );
    return toFileSystemItem(existing, target.virtual);
  }

  /**
   * Uses Graph's upload-session (chunked) flow rather than a simple PUT: simple PUT is capped at
   * 4MB, and this file-manager's realistic content (photos, videos, CAD drawings) regularly
   * exceeds that.
   */
  async uploadFile(
    target: MountTarget,
    file: File,
  ): Promise<FileSystemItem> {
    const token = await sharePointAuthService.acquireGraphToken();

    const sessionEndpoint = buildDriveEndpoint(
      target.driveId,
      target.rest,
      'createUploadSession',
      file.name,
    );

    const { uploadUrl }: GraphUploadSession = await withRetry(async () => {
      const sessionResponse = await fetch(sessionEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Never overwrite an existing file with the same name — SharePoint appends a suffix
        // instead.
        body: JSON.stringify({
          item: { '@microsoft.graph.conflictBehavior': 'rename' },
        }),
      });

      if (!sessionResponse.ok) {
        throw graphError(
          'Failed to create SharePoint upload session',
          sessionResponse,
        );
      }

      return (await sessionResponse.json()) as GraphUploadSession;
    }, GRAPH_RETRY_OPTIONS);

    // The chunk loop itself lives in services/graph/upload-in-chunks.ts — the protocol is Graph's,
    // not this drive's, and the Outlook attachment path uses the identical one. It hands back the
    // final PUT's response unread: only that last PUT returns the created DriveItem (200/201), while
    // intermediate chunks answer 202 with a session-status body, so parsing is the caller's job.
    const response = await uploadInChunks(uploadUrl, file, {
      chunkSize: UPLOAD_CHUNK_SIZE_BYTES,
    });
    const lastItem = (await response.json()) as GraphDriveItem;

    // The response body's name/path (not file.name) is the source of truth here: a "rename"
    // conflictBehavior may have suffixed the actual stored name.
    return toFileSystemItem(lastItem, target.virtual);
  }

  async getThumbnail(
    driveId: string,
    itemId: string,
  ): Promise<{ url: string } | null> {
    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}/thumbnails`;

    const response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // No thumbnail available and a failed request are treated identically here (both fall back
    // to the extension icon), so this deliberately returns null rather than throwing.
    if (!response.ok) return null;

    const body: GraphThumbnailResponse = await response.json();
    // "large" (max 800x800) rather than "medium" (max 176x176) — the details panel now shows
    // this at full panel width, and medium's cap was visibly upscaling/blurring past ~176px.
    const url = body.value[0]?.large?.url;
    return url ? { url } : null;
  }

  async getItemDetails(
    driveId: string,
    itemId: string,
  ): Promise<FileItemDetails | null> {
    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}`;

    const response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Enrichment data for the details panel, not core navigation data — a failed request falls
    // back to hiding the metadata rows/Download button rather than surfacing an error, so this
    // deliberately returns null rather than throwing.
    if (!response.ok) return null;

    const body: GraphDriveItemDetails = await response.json();
    return {
      size: body.size,
      lastModifiedDateTime: body.lastModifiedDateTime,
      downloadUrl: body['@microsoft.graph.downloadUrl'],
    };
  }

  /**
   * Resolves a file's pre-signed download URL — the one thing needed to read its BYTES rather than its
   * metadata.
   *
   * Deliberately NOT getItemDetails() above, which swallows every failure and returns null by design
   * because it feeds optional details-panel enrichment. Here a missing URL is a real per-file error that
   * has to reach the caller with a reason it can report, so this throws.
   *
   * And deliberately NOT folded into getChildren's $select. The URL is a short-lived signed credential
   * (~1 hour), not metadata, and useDirectoryChildrenQuery is a vue-query cache: a listing left open
   * across lunch would hand back expired URLs and 403 at exactly the moment the user clicks.
   * useFilePreviewQuery already sets staleTime/gcTime to 0 for precisely this reason.
   *
   * One request per item. $batch (up to 20 sub-requests in a single POST) is the upgrade path when
   * selections get large; deliberately not built now.
   */
  async getDownloadUrl(
    driveId: string,
    itemId: string,
    signal?: AbortSignal,
  ): Promise<string> {
    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}?$select=@microsoft.graph.downloadUrl`;

    return withRetry(
      async () => {
        const response = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          ...(signal ? { signal } : {}),
        });

        if (!response.ok) {
          throw graphError('Failed to resolve SharePoint download URL', response);
        }

        const body = (await response.json()) as GraphDriveItemDetails;
        const url = body['@microsoft.graph.downloadUrl'];
        if (!url) {
          // A 200 with no URL — a folder, or an item with no readable content. Raised as a
          // GraphRequestError anyway, and as a 404, because callers classify failures on `.status`;
          // inventing a bare Error here would make this the one per-file failure with no reason to
          // report. 404 is also not retryable, which is correct: re-asking cannot conjure a URL.
          throw new GraphRequestError('SharePoint item has no download URL', 404);
        }
        return url;
      },
      { ...GRAPH_RETRY_OPTIONS, signal },
    );
  }

  async getPreview(driveId: string, itemId: string): Promise<FilePreview> {
    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}/preview`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    // Unlike getThumbnail/getItemDetails above, this is a direct response to an explicit user
    // click (opening the preview modal), not a passive background fetch — so a failure needs to
    // surface as a real error the caller can distinguish from "still loading", rather than a
    // silent null fallback.
    if (!response.ok) {
      throw graphError('Failed to fetch preview', response);
    }

    return (await response.json()) as FilePreview;
  }

  /**
   * Moves a driveItem by PATCHing its parentReference — pure-id addressing on both the source
   * (drives/{driveId}/items/{id}) and destination (parentReference.driveId/id), unlike every other
   * method above which still addresses by path. Requires the caller to already have resolved the
   * destination via getItemReference().
   *
   * '@microsoft.graph.conflictBehavior': 'fail' is explicit here — unlike createFolder/uploadFile,
   * a move must never silently overwrite or merge into an existing item at the destination. A 409
   * is therefore fatal and NOT retried (it's not in RETRYABLE_STATUSES), so it propagates as a
   * GraphRequestError for the caller (the move mutation) to surface as an error toast.
   */
  async moveItem(
    sourceItem: FileSystemItem,
    destParent: ItemReference,
    destParentPath: string,
  ): Promise<FileSystemItem> {
    if (!sourceItem.id || !sourceItem.driveId) {
      throw new Error('moveItem: source item is missing id/driveId');
    }

    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${sourceItem.driveId}/items/${sourceItem.id}`;

    const patched: GraphDriveItem = await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentReference: { driveId: destParent.driveId, id: destParent.id },
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });

      if (!response.ok) {
        throw graphError('Failed to move SharePoint item', response);
      }

      return (await response.json()) as GraphDriveItem;
    }, GRAPH_RETRY_OPTIONS);

    return toFileSystemItem(patched, destParentPath);
  }

  /**
   * Copies a driveItem, including ACROSS drives — which is the whole reason it exists alongside
   * moveItem: PATCHing parentReference is single-drive only, so a drop from one document library
   * into the other can only ever be a copy.
   *
   * Asynchronous, unlike every other method here: the POST returns 202 Accepted with a `Location`
   * header naming a monitor resource, and the copy's real outcome (including a name collision) only
   * shows up there. This method polls that monitor to a terminal state and resolves with the created
   * item, so callers see the same shape as moveItem and need no lifecycle of their own.
   *
   * `options.signal` abandons the POLL, never the copy: once the 202 is returned SharePoint completes
   * the work server-side regardless. Abandoning therefore rejects with reason 'abandoned', which
   * callers must not report as a failure.
   *
   * `options.name` renames the copy as it is created — what an in-place duplicate needs, since a copy
   * into the source's own folder would otherwise collide with the original under conflictBehavior
   * 'fail'. Omitted by the cross-drive drop path, which keeps the source's name at the destination.
   */
  async copyItem(
    sourceItem: FileSystemItem,
    destParent: ItemReference,
    destParentPath: string,
    options: { name?: string; signal?: AbortSignal } = {},
  ): Promise<FileSystemItem> {
    if (!sourceItem.id || !sourceItem.driveId) {
      throw new Error('copyItem: source item is missing id/driveId');
    }

    const { name, signal } = options;
    const token = await sharePointAuthService.acquireGraphToken();
    // conflictBehavior goes in the QUERY STRING here, not the body as moveItem/createFolder/
    // uploadFile set it — the copy action is documented that way and ignores a body-level value.
    // 'fail' for the same reason move uses it: a transfer must never silently overwrite or merge.
    // Unlike move it does NOT surface as a 409 — see #pollCopyMonitor. Kept as 'fail' even when a
    // `name` is supplied: 'rename' would let SharePoint auto-suffix and silently discard the name the
    // user chose, which is precisely the thing they were asked for.
    const endpoint = `${GRAPH_BASE_URL}/drives/${sourceItem.driveId}/items/${sourceItem.id}/copy?@microsoft.graph.conflictBehavior=fail`;

    const monitorUrl: string = await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentReference: { driveId: destParent.driveId, id: destParent.id },
          // Spread rather than always-present: an explicit `name: undefined` would be dropped by
          // JSON.stringify anyway, but this keeps the no-name request body byte-identical to what the
          // drag path sent before this option existed.
          ...(name ? { name } : {}),
        }),
      });

      if (!response.ok) {
        throw graphError('Failed to start SharePoint copy', response);
      }

      const location = response.headers?.get?.('Location');
      if (!location) {
        throw new CopyMonitorError(
          'failed',
          undefined,
          'SharePoint copy returned no monitor URL',
        );
      }
      return location;
    }, GRAPH_RETRY_OPTIONS);

    const resourceId = await this.#pollCopyMonitor(monitorUrl, signal);

    // The monitor hands back an id and nothing else, so the created item is fetched to build the
    // FileSystemItem — addressed on the DESTINATION drive, which is not the source's.
    const copied = await this.#getDriveItemById(destParent.driveId, resourceId);
    return toFileSystemItem(copied, destParentPath);
  }

  /**
   * Polls a copy monitor until it reports a terminal state, and returns the new item's id.
   *
   * Deliberately not withRetry(): that layer re-runs a call that FAILED, whereas every request here
   * succeeds and we are waiting on a status field to change. Throttling is the one overlap — a 429
   * on the monitor means "not terminal yet", so it backs off (honouring Retry-After) rather than
   * failing.
   */
  async #pollCopyMonitor(
    monitorUrl: string,
    signal?: AbortSignal,
  ): Promise<string> {
    const deadline = Date.now() + COPY_TIMEOUT_MS;
    let delayMs = COPY_POLL_INITIAL_DELAY_MS;

    for (;;) {
      if (signal?.aborted) throw new CopyMonitorError('abandoned');
      if (Date.now() >= deadline) throw new CopyMonitorError('timeout');

      // No Authorization header: the monitor URL is pre-authenticated by Graph and lives on another
      // host — the same property the upload-session URL relies on. A bearer token is rejected there.
      let response: Response;
      try {
        response = await fetch(monitorUrl, signal ? { signal } : {});
      } catch (error) {
        if (signal?.aborted) throw new CopyMonitorError('abandoned');
        throw error;
      }

      if (response.ok) {
        const body = (await response.json()) as GraphCopyMonitor;

        if (body.status === 'completed') {
          if (!body.resourceId) {
            throw new CopyMonitorError(
              'failed',
              undefined,
              'SharePoint copy completed without a resource id',
            );
          }
          return body.resourceId;
        }

        if (body.status === 'failed') {
          throw new CopyMonitorError(
            'failed',
            body.error?.code,
            body.error?.message,
          );
        }

        await sleep(delayMs, signal);
        delayMs = Math.min(delayMs * COPY_POLL_BACKOFF_FACTOR, COPY_POLL_MAX_DELAY_MS);
        continue;
      }

      if (!RETRYABLE_STATUSES.has(response.status)) {
        throw graphError('Failed to poll SharePoint copy monitor', response);
      }

      await sleep(parseRetryAfterMs(response.headers) ?? delayMs, signal);
      delayMs = Math.min(delayMs * COPY_POLL_BACKOFF_FACTOR, COPY_POLL_MAX_DELAY_MS);
    }
  }

  /**
   * Deletes a driveItem outright — pure-id addressing (drives/{driveId}/items/{id}), same guard as
   * moveItem above. Graph deletes a folder's entire subtree server-side, so unlike upload's
   * tree-flattening there's no client-side recursive walk to do here: one request handles a whole
   * directory regardless of how deep its contents go.
   *
   * Success is 204 No Content — no body to parse, hence the Promise<void> return. Any non-2xx is a
   * fatal graphError with no special-case status handling: unlike move/createFolder, delete has no
   * 409-equivalent "already exists" concept to recover from.
   */
  async deleteItem(item: FileSystemItem): Promise<void> {
    if (!item.id || !item.driveId) {
      throw new Error('deleteItem: item is missing id/driveId');
    }

    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${item.driveId}/items/${item.id}`;

    await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw graphError('Failed to delete SharePoint item', response);
      }
    }, GRAPH_RETRY_OPTIONS);
  }

  /**
   * Renames a driveItem by PATCHing its `name` — pure-id addressing (drives/{driveId}/items/{id}),
   * same guard as moveItem/deleteItem above. Unlike moveItem, the item's parent never changes, so
   * there's no destination reference to resolve first — the returned item is re-parented under the
   * source item's own (unchanged) parent path.
   *
   * '@microsoft.graph.conflictBehavior': 'fail' is explicit here for the same reason moveItem sets
   * it: a rename must never silently overwrite or merge into an existing sibling. A 409 is
   * therefore fatal and NOT retried (it's not in RETRYABLE_STATUSES), so it propagates as a
   * GraphRequestError for the caller (the rename mutation) to surface as an error toast.
   */
  async renameItem(item: FileSystemItem, newName: string): Promise<FileSystemItem> {
    if (!item.id || !item.driveId) {
      throw new Error('renameItem: item is missing id/driveId');
    }

    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${item.driveId}/items/${item.id}`;

    const patched: GraphDriveItem = await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newName,
          '@microsoft.graph.conflictBehavior': 'fail',
        }),
      });

      if (!response.ok) {
        throw graphError('Failed to rename SharePoint item', response);
      }

      return (await response.json()) as GraphDriveItem;
    }, GRAPH_RETRY_OPTIONS);

    return toFileSystemItem(patched, getParentPath(item.path));
  }

  /**
   * Resolves a target to the {driveId, id} pair Graph needs to address it as a move destination.
   * Standalone (rather than folded into moveItem) so Phase 3 can reuse it independently.
   *
   * A drive root (`rest === '/'`, i.e. a library's own root) reuses getDriveRoot()'s memoised cache —
   * often zero extra network. Nested paths use the suffix-less item-by-path form
   * (`drives/{driveId}/root:{rest}`, no trailing `:/{suffix}`) — deliberately NOT
   * buildDriveEndpoint(), which always appends a `:/{suffix}` segment.
   */
  async getItemReference(target: MountTarget): Promise<ItemReference> {
    if (target.rest === '/') {
      const { driveId, rootId } = await this.getDriveRoot(target.driveId);
      return { driveId, id: rootId };
    }

    const body = await this.#getDriveItemByPath(
      target.driveId,
      target.rest,
      'Failed to resolve SharePoint item reference',
    );

    return { driveId: body.parentReference?.driveId ?? '', id: body.id ?? '' };
  }

  /**
   * GETs a single driveItem by its in-drive path via the suffix-less item-by-path form
   * (`drives/{driveId}/root:{rest}`, no trailing `:/{suffix}`) — deliberately NOT
   * buildDriveEndpoint(), which always appends a `:/{suffix}` segment. Shared by getItemReference()
   * and createFolder()'s 409 (already-exists) recovery. Callers pass their own error message so
   * failures read in their context. Never called for a drive root ("/") — that has no path to
   * address.
   */
  /**
   * GETs a single driveItem by id. Sibling to #getDriveItemByPath below, for the one caller that has
   * an id and no path: a completed copy's monitor reports only `resourceId`.
   */
  async #getDriveItemById(
    driveId: string,
    itemId: string,
  ): Promise<GraphDriveItem> {
    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/items/${itemId}`;

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw graphError('Failed to resolve copied SharePoint item', response);
      }

      return (await response.json()) as GraphDriveItem;
    }, GRAPH_RETRY_OPTIONS);
  }

  async #getDriveItemByPath(
    driveId: string,
    rest: string,
    errorMessage: string,
  ): Promise<GraphDriveItem> {
    const token = await sharePointAuthService.acquireGraphToken();
    const endpoint = `${GRAPH_BASE_URL}/drives/${driveId}/root:${encodePathSegments(rest)}`;

    return withRetry(async () => {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw graphError(errorMessage, response);
      }

      return (await response.json()) as GraphDriveItem;
    }, GRAPH_RETRY_OPTIONS);
  }
}

export const sharePointDriveService = new SharePointDriveService();
